import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Image, Input, InputNumber, Pagination, Select, Space, Table, Tag } from 'antd';
import { SaveOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { LayoutContext } from '../../contexts';
import { useFetch } from '../../hooks';
import { URLS } from '../../constants';
import { usePricingTierProducts } from '../../hooks/useMenuQuery';
import { getImageFallbackUrl } from '../../utils/helpers';

const currencyOptions = ['USD', 'IQD', 'EUR', 'SAR', 'SYP'];

function PricingTierDetail() {
  const { tierId } = useParams();
  const { selectedLanguage, languages, setHeaderComponent } = useContext(LayoutContext);
  const { doFetch } = useFetch();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editedValues, setEditedValues] = useState({});

  const searchParams = useMemo(
    () => ({
      page,
      per_page: perPage,
      ...(searchQuery.trim() && { q: searchQuery.trim() }),
    }),
    [page, perPage, searchQuery]
  );

  const { data, isLoading, refetch } = usePricingTierProducts(tierId, searchParams);
  const tier = data?.tier;
  const products = data?.products || [];

  useEffect(() => {
    setHeaderComponent(<b>{tier ? `Tier Pricing - ${tier.name}` : 'Tier Pricing'}</b>);
    return () => setHeaderComponent(null);
  }, [setHeaderComponent, tier]);

  const getTranslationName = product => {
    const translation =
      product.product_translations?.find(t => t.language_id === selectedLanguage) ||
      product.product_translations?.[0];
    return translation?.name_text || `Product #${product.id}`;
  };

  const getVariantLabel = (item, baseKey) => {
    if (!item) return 'Unnamed';
    const languageId = selectedLanguage || languages?.[0]?.id || 1;
    const languageFallbackId = languages?.[0]?.id || 1;
    return (
      item[`${baseKey}.${languageId}`] ||
      item[`${baseKey}.${languageFallbackId}`] ||
      item.name ||
      item.id ||
      'Unnamed'
    );
  };

  const startEditing = product => {
    const tierPricing = product.tier_pricing || {};
    const productOverride = tierPricing.product_override || {};
    const items = tierPricing.items || {};

    setEditingProductId(product.id);
    setEditedValues({
      product_override: {
        price: productOverride.price ?? null,
        currency: productOverride.currency ?? null,
        discount: productOverride.discount ?? null,
        discount_valid_until: productOverride.discount_valid_until ?? null,
      },
      item_overrides: {
        types: (items.types || []).map(entry => ({
          id: entry?.item_key || entry?.item?.id,
          price: entry?.override?.price ?? null,
          currency: entry?.override?.currency ?? null,
        })),
        sizes: (items.sizes || []).map(entry => ({
          id: entry?.item_key || entry?.item?.id,
          price: entry?.override?.price ?? null,
          currency: entry?.override?.currency ?? null,
          discount: entry?.override?.discount ?? null,
        })),
        addons: (items.addons || []).map(entry => ({
          id: entry?.item_key || entry?.item?.id,
          price: entry?.override?.price ?? null,
          currency: entry?.override?.currency ?? null,
        })),
        colors: (items.colors || []).map((entry, index) => ({
          id: entry?.item_key || entry?.item?.id || `idx:${index}`,
          price: entry?.override?.price ?? null,
          currency: entry?.override?.currency ?? null,
        })),
      },
    });
  };

  const updateProductOverrideField = (field, value) => {
    setEditedValues(prev => ({
      ...prev,
      product_override: {
        ...(prev.product_override || {}),
        [field]: value,
      },
    }));
  };

  const updateItemOverrideField = (kind, index, field, value) => {
    setEditedValues(prev => {
      const next = { ...prev };
      const currentList = [...(next.item_overrides?.[kind] || [])];
      currentList[index] = { ...(currentList[index] || {}), [field]: value };
      return {
        ...next,
        item_overrides: {
          ...(next.item_overrides || {}),
          [kind]: currentList,
        },
      };
    });
  };

  const clearProductOverride = () => {
    setEditedValues(prev => ({
      ...prev,
      product_override: {
        price: null,
        currency: null,
        discount: null,
        discount_valid_until: null,
      },
    }));
  };

  const clearItemOverride = (kind, index) => {
    setEditedValues(prev => {
      const next = { ...prev };
      const currentList = [...(next.item_overrides?.[kind] || [])];
      const current = currentList[index] || {};
      currentList[index] = {
        id: current.id,
        price: null,
        currency: null,
        ...(kind === 'sizes' ? { discount: null } : {}),
      };
      return {
        ...next,
        item_overrides: {
          ...(next.item_overrides || {}),
          [kind]: currentList,
        },
      };
    });
  };

  const saveRow = productId => {
    doFetch({
      url: URLS.updatePricingTierProductOverrides({ tierId, productId }),
      method: 'PUT',
      params: editedValues,
      onSuccess: () => {
        setEditingProductId(null);
        setEditedValues({});
        refetch();
      },
      successMessage: 'Tier pricing updated successfully',
      showSuccessNotification: true,
    });
  };

  const variantColumns = (kind, baseNameKey) => [
    {
      title: 'Name',
      key: 'name',
      render: (_, row) => getVariantLabel(row.item, baseNameKey),
    },
    ...(kind === 'colors'
      ? [
          {
            title: 'Color',
            key: 'colorPreview',
            render: (_, row) => {
              const hexcode = row?.item?.hexcode || '#cccccc';
              const imageLink = row?.item?.imageLink;
              return (
                <Space>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: '1px solid #d9d9d9',
                      backgroundColor: hexcode,
                    }}
                    title={hexcode}
                  />
                  <span>{hexcode}</span>
                  {imageLink ? (
                    <Image
                      width={36}
                      height={36}
                      src={imageLink}
                      fallback={getImageFallbackUrl(imageLink)}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <span>-</span>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
    {
      title: 'Base',
      key: 'base',
      render: (_, row) => `${row.base_price ?? '-'} ${row.base_currency || ''}`,
    },
    {
      title: 'Tier Price',
      key: 'tierPrice',
      render: (_, row, index) => {
        if (editingProductId !== row.product_id) {
          return `${row.effective_price ?? '-'} ${row.effective_currency || ''}`;
        }
        const edited = editedValues?.item_overrides?.[kind]?.[index] || {};
        return (
          <Space>
            <Input
              style={{ width: 110 }}
              value={edited.price}
              placeholder="inherit"
              onChange={e => updateItemOverrideField(kind, index, 'price', e.target.value || null)}
            />
            <Select
              style={{ width: 95 }}
              value={edited.currency}
              allowClear
              placeholder="inherit"
              onChange={value => updateItemOverrideField(kind, index, 'currency', value || null)}
            >
              {currencyOptions.map(currency => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
            <Button size="small" onClick={() => clearItemOverride(kind, index)}>
              Inherit
            </Button>
          </Space>
        );
      },
    },
    ...(kind === 'sizes'
      ? [
          {
            title: 'Tier Discount',
            key: 'tierDiscount',
            render: (_, row, index) => {
              if (editingProductId !== row.product_id) {
                return row.effective_discount ?? '-';
              }
              const edited = editedValues?.item_overrides?.sizes?.[index] || {};
              return (
                <InputNumber
                  min={0}
                  max={100}
                  value={edited.discount}
                  onChange={value => updateItemOverrideField('sizes', index, 'discount', value ?? null)}
                />
              );
            },
          },
        ]
      : []),
  ];

  const expandedRowRender = record => {
    const items = record?.tier_pricing?.items || {};
    const enrich = list =>
      (list || []).map((item, index) => ({
        ...item,
        product_id: record.id,
        _row_key: item.item_key || item.item?.id || `idx:${index}`,
      }));

    const types = enrich(items.types);
    const sizes = enrich(items.sizes);
    const addons = enrich(items.addons);
    const colors = enrich(items.colors);

    return (
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {types.length > 0 && (
          <Card size="small" title="Types">
            <Table
              rowKey={row => `type-${record.id}-${row._row_key}`}
              dataSource={types}
              columns={variantColumns('types', 'typeName')}
              pagination={false}
              size="small"
            />
          </Card>
        )}
        {sizes.length > 0 && (
          <Card size="small" title="Sizes">
            <Table
              rowKey={row => `size-${record.id}-${row._row_key}`}
              dataSource={sizes}
              columns={variantColumns('sizes', 'sizeName')}
              pagination={false}
              size="small"
            />
          </Card>
        )}
        {addons.length > 0 && (
          <Card size="small" title="Addons">
            <Table
              rowKey={row => `addon-${record.id}-${row._row_key}`}
              dataSource={addons}
              columns={variantColumns('addons', 'addonName')}
              pagination={false}
              size="small"
            />
          </Card>
        )}
        {colors.length > 0 && (
          <Card size="small" title="Colors">
            <Table
              rowKey={row => `color-${record.id}-${row._row_key}`}
              dataSource={colors}
              columns={variantColumns('colors', 'colorName')}
              pagination={false}
              size="small"
            />
          </Card>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <Space>
          <Image
            width={44}
            height={44}
            src={record.image_url}
            fallback={getImageFallbackUrl(record.image_url)}
            style={{ objectFit: 'cover', borderRadius: 6 }}
          />
          <Space direction="vertical" size={0}>
            <span>{getTranslationName(record)}</span>
            <Tag>{record.id}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Base Price',
      key: 'base_price',
      render: (_, record) =>
        `${record?.tier_pricing?.base_product?.price ?? record.price ?? '-'} ${
          record?.tier_pricing?.base_product?.currency ?? record.currency ?? ''
        }`,
    },
    {
      title: 'Tier Price',
      key: 'tier_price',
      render: (_, record) => {
        const effective = record?.tier_pricing?.effective_product || {};
        if (editingProductId !== record.id) {
          return `${effective.price ?? '-'} ${effective.currency ?? ''}`;
        }
        return (
          <Space>
            <Input
              style={{ width: 110 }}
              value={editedValues?.product_override?.price}
              placeholder="inherit"
              onChange={e => updateProductOverrideField('price', e.target.value || null)}
            />
            <Select
              style={{ width: 95 }}
              value={editedValues?.product_override?.currency}
              allowClear
              placeholder="inherit"
              onChange={value => updateProductOverrideField('currency', value || null)}
            >
              {currencyOptions.map(currency => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Space>
        );
      },
    },
    {
      title: 'Tier Discount',
      key: 'tier_discount',
      render: (_, record) => {
        const effectiveDiscount = record?.tier_pricing?.effective_product?.discount ?? 0;
        if (editingProductId !== record.id) {
          return `${effectiveDiscount}%`;
        }
        return (
          <InputNumber
            min={0}
            max={100}
            value={editedValues?.product_override?.discount}
            onChange={value => updateProductOverrideField('discount', value ?? null)}
          />
        );
      },
    },
    {
      title: 'Tier Discount Valid Until',
      key: 'tier_discount_valid_until',
      render: (_, record) => {
        const validUntil = record?.tier_pricing?.effective_product?.discount_valid_until;
        if (editingProductId !== record.id) {
          return validUntil || '-';
        }
        const value = editedValues?.product_override?.discount_valid_until;
        return (
          <DatePicker
            allowClear
            value={value ? dayjs(value) : null}
            onChange={date => updateProductOverrideField('discount_valid_until', date ? date.format('YYYY-MM-DD') : null)}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (editingProductId === record.id) {
          return (
            <Space>
              <Button type="primary" icon={<SaveOutlined />} onClick={() => saveRow(record.id)}>
                Save
              </Button>
              <Button onClick={clearProductOverride}>Inherit Product</Button>
              <Button onClick={() => setEditingProductId(null)}>Cancel</Button>
            </Space>
          );
        }
        return (
          <Button onClick={() => startEditing(record)}>
            Edit Tier Pricing
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Card title={tier ? `Tier: ${tier.name} (${tier.code})` : 'Tier Pricing'} style={{ marginBottom: 16 }}>
        <Space>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search products by translation"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: 320 }}
            allowClear
          />
          <Button onClick={() => refetch()}>Search</Button>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={products}
        columns={columns}
        expandable={{ expandedRowRender }}
        pagination={false}
      />

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <Pagination
          current={page}
          pageSize={perPage}
          total={data?.total || 0}
          showSizeChanger
          pageSizeOptions={['10', '20', '30', '50', '100']}
          onChange={(nextPage, nextPageSize) => {
            setPage(nextPage);
            if (nextPageSize !== perPage) setPerPage(nextPageSize);
          }}
        />
      </div>
    </div>
  );
}

export default PricingTierDetail;
